import { useParams, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import {
  GitFork, ShieldQuestion, Share2, Download, Pencil, ArrowLeft, Maximize2, Minimize2,
  Network, AlignLeft, Scale, GitBranch, GitCompareArrows, BarChart3, MessageSquare,
} from "lucide-react";
import { useStore } from "@/store/useStore";
import { cn } from "@/lib/cn";
import { Button } from "@/components/Button";
import { Badge, TagChip } from "@/components/Badge";
import { Avatar, EmptyState, Spinner } from "@/components/AppShell";
import { GraphCanvas } from "@/components/flow/GraphCanvas";
import { DetailPanel } from "@/components/DetailPanel";
import { useToast } from "@/components/Toast";
import { CanvasView } from "@/components/views/CanvasView";
import { SpineView } from "@/components/views/SpineView";
import { CruxView } from "@/components/views/CruxView";
import { LineageView } from "@/components/views/LineageView";
import { DiffView } from "@/components/views/DiffView";
import { StatsView } from "@/components/views/StatsView";
import { CommentsView } from "@/components/views/CommentsView";
import type { StrainGraph } from "@/types";

type Mode = "canvas" | "spine" | "cruxes" | "lineage" | "diff" | "stats" | "comments";

const MODES: { key: Mode; label: string; icon: React.ReactNode }[] = [
  { key: "canvas", label: "Canvas", icon: <Network size={14} /> },
  { key: "spine", label: "Spine", icon: <AlignLeft size={14} /> },
  { key: "cruxes", label: "Cruxes", icon: <Scale size={14} /> },
  { key: "lineage", label: "Lineage", icon: <GitBranch size={14} /> },
  { key: "diff", label: "Diff", icon: <GitCompareArrows size={14} /> },
  { key: "stats", label: "Stats", icon: <BarChart3 size={14} /> },
  { key: "comments", label: "Comments", icon: <MessageSquare size={14} /> },
];

export default function StrainExplorer() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const store = useStore();
  const toast = useToast((s) => s.push);
  const [mode, setMode] = useState<Mode>("canvas");
  const [graphLoading, setGraphLoading] = useState(true);
  const [canvasFullscreen, setCanvasFullscreen] = useState(false);

  const graph = useStore((s) => slug ? s.getGraph(slug) : undefined);
  const loadGraph = useStore((s) => s.loadGraph);

  // reset to canvas when switching strains
  useEffect(() => { setMode("canvas"); store.selectNode(null); store.selectEdge(null); }, [slug]);

  useEffect(() => {
    if (!slug) return;
    setGraphLoading(true);
    loadGraph(slug).finally(() => setGraphLoading(false));
  }, [slug, loadGraph]);

  // esc to exit canvas fullscreen
  useEffect(() => {
    if (!canvasFullscreen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setCanvasFullscreen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [canvasFullscreen]);

  if (!graph) {
    if (graphLoading) {
      return (
        <div className="p-8">
          <div className="flex items-center gap-2 text-[13px] text-[var(--color-ink-muted)]">
            <Spinner /> Loading strain...
          </div>
        </div>
      );
    }
    return (
      <div className="p-8">
        <EmptyState
          icon={<Network size={28} />}
          title="Strain not found"
          desc="This strain doesn't exist or was removed."
          action={<Button onClick={() => navigate("/app")}>Back to feed</Button>}
        />
      </div>
    );
  }

  const strain = graph.strain;

  const handleFork = () => {
    store.forkStrain(strain.slug, "mutation", "strain", "Full strain fork.").then((newSlug) => {
      toast({ tone: "ok", title: "Fork created", desc: "A copy you can edit freely. Switch to Diff to see changes." });
      navigate(`/s/${newSlug}`);
    });
  };

  const handleExport = () => {
    const json = JSON.stringify(graph, null, 2);
    const blob = new Blob([json], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${strain.slug}.json`;
    a.click();
    URL.revokeObjectURL(url);
    toast({ tone: "ok", title: "Exported", desc: "Strain graph downloaded as JSON." });
  };

  const isMobile = typeof window !== "undefined" && window.innerWidth < 768;
  const showDetail = (mode === "canvas") && (store.selectedNodeId || store.selectedEdgeId);

  return (
    <div className="h-[calc(100vh-3.5rem)] flex flex-col relative">
      {/* header — hidden in canvas fullscreen */}
      {!canvasFullscreen && (
      <div className="shrink-0 border-b border-[var(--color-border)] bg-[var(--color-surface)]/60 backdrop-blur">
        <div className="px-4 py-3">
          <div className="flex items-start gap-3">
            <button onClick={() => navigate(-1)} className="text-[var(--color-ink-muted)] hover:text-[var(--color-ink)] mt-1">
              <ArrowLeft size={18} />
            </button>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                {strain.status_badges?.map((b) => <Badge key={b} tone="muted">{b.replace(/_/g, " ")}</Badge>)}
                <Badge tone="muted">v{graph.version.version_number}</Badge>
                {graph.version.fork_intent && (
                  <Badge tone="inferred">{graph.version.fork_intent.replace(/_/g, " ")} fork</Badge>
                )}
                <span className="text-[11px] text-[var(--color-ink-faint)] font-mono ml-auto">{strain.visibility}</span>
              </div>
              <h1 className="text-xl font-semibold tracking-tight leading-tight">{strain.title}</h1>
              <p className="text-[13px] text-[var(--color-ink-muted)] mt-1 leading-relaxed">{strain.summary}</p>
              <div className="flex items-center gap-2 mt-2 flex-wrap">
                <Avatar initials={strain.creator_avatar ?? strain.creator.slice(0, 2).toUpperCase()} size={20} />
                <span className="text-[12px] text-[var(--color-ink-soft)]">{strain.creator}</span>
                <span className="text-[11px] text-[var(--color-ink-faint)]">·</span>
                <span className="text-[11px] text-[var(--color-ink-faint)] font-mono">{graph.nodes.length} nodes · {graph.edges.length} edges</span>
                <div className="flex gap-1 ml-2">
                  {strain.tags.map((t) => <TagChip key={t}>{t}</TagChip>)}
                </div>
              </div>
            </div>
          </div>

          {/* actions */}
          <div className="flex items-center gap-2 mt-3 flex-wrap">
            <Button size="sm" leftIcon={<GitFork size={13} />} onClick={handleFork}>Fork</Button>
            <Button size="sm" variant="outline" leftIcon={<ShieldQuestion size={13} />} onClick={() => { store.selectNode(graph.nodes[0]?.id ?? null); toast({ tone: "info", title: "Pick a target", desc: "Select a node or edge, then Challenge." }); }}>Challenge</Button>
            <Button size="sm" variant="outline" leftIcon={<Share2 size={13} />} onClick={() => { navigator.clipboard?.writeText(window.location.href); toast({ tone: "ok", title: "Link copied" }); }}>Share</Button>
            <Button size="sm" variant="outline" leftIcon={<Download size={13} />} onClick={handleExport}>Export</Button>
            <Button size="sm" variant="ghost" leftIcon={<Pencil size={13} />} onClick={() => toast({ tone: "info", title: "Edit mode", desc: "Drag nodes, double-click to add, use the panel to edit." })}>Edit</Button>
            <Button size="sm" variant={canvasFullscreen ? "primary" : "ghost"} leftIcon={canvasFullscreen ? <Minimize2 size={13} /> : <Maximize2 size={13} />} onClick={() => setCanvasFullscreen((v) => !v)}>
              {canvasFullscreen ? "Exit fullscreen" : "Fullscreen"}
            </Button>
          </div>
        </div>

        {/* mode tabs */}
        <div className="flex items-center gap-0.5 px-4 overflow-x-auto no-scrollbar">
          {MODES.map((m) => (
            <button
              key={m.key}
              onClick={() => setMode(m.key)}
              className={cn(
                "inline-flex items-center gap-1.5 h-9 px-3 text-[13px] border-b-2 transition-colors whitespace-nowrap",
                mode === m.key
                  ? "border-[var(--color-primary)] text-[var(--color-ink)]"
                  : "border-transparent text-[var(--color-ink-muted)] hover:text-[var(--color-ink)]",
              )}
            >
              {m.icon}
              {m.label}
            </button>
          ))}
        </div>
      </div>
      )}

      {/* body */}
      <div className="flex-1 min-h-0 flex">
        <div className="flex-1 min-w-0 relative">
          {mode === "canvas" && <CanvasView graph={graph} fullscreen={canvasFullscreen} onToggleFullscreen={() => setCanvasFullscreen((v) => !v)} />}
          {mode === "spine" && <SpineView graph={graph} />}
          {mode === "cruxes" && <CruxView graph={graph} />}
          {mode === "lineage" && <LineageView graph={graph} />}
          {mode === "diff" && <DiffView graph={graph} />}
          {mode === "stats" && <StatsView graph={graph} />}
          {mode === "comments" && <CommentsView graph={graph} />}
        </div>

        {/* detail panel (canvas mode only) */}
        {mode === "canvas" && !canvasFullscreen && (
          <div className={cn(
            "shrink-0 border-l border-[var(--color-border)] bg-[var(--color-surface)] transition-all duration-200",
            showDetail ? "w-[360px]" : "w-0",
          )}>
            {showDetail && (
              <div className="w-[360px] h-full">
                <DetailPanel graph={graph} />
              </div>
            )}
          </div>
        )}
      </div>

      {/* fullscreen detail panel overlay */}
      {mode === "canvas" && canvasFullscreen && showDetail && (
        <div className="absolute top-0 right-0 bottom-0 w-[360px] z-40 border-l border-[var(--color-border)] bg-[var(--color-surface)] shadow-pop">
          <DetailPanel graph={graph} />
        </div>
      )}
    </div>
  );
}
